import React, { useState, useEffect, useContext, useCallback } from "react";
import { Container, Table, Button, FormControl, Form } from "react-bootstrap";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import Contexts from "../../context/Contexts";
import * as XLSX from 'xlsx';

export default function Vales() {
  const [vales, setVales] = useState([]);
  const [buscarSucursal, setBuscarSucursal] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [valesPorPagina, setValesPorPagina] = useState(10);
  const [columnaOrden, setColumnaOrden] = useState(null);
  const [direccionOrden, setDireccionOrden] = useState("asc");
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [valesOriginales, setValesOriginales] = useState([]);

  const contexto = useContext(Contexts.DataContext);

  const apiUrl = process.env.REACT_APP_API_URL;

  const formatearFecha = (fecha) => {
    if (!fecha) return "";

    const partes = String(fecha).split("-");

    if (partes.length !== 3) {
      return fecha;
    }

    const [anio, mes, dia] = partes;

    return `${dia}/${mes}/${anio}`;
  };

  const manejadorFiltroClienteSeleccionado = useCallback(() => {
    let valesFiltrados = [...valesOriginales];
    if (clienteSeleccionado) {
      valesFiltrados = valesFiltrados.filter(
        (vale) => parseInt(vale.cliente_id) === parseInt(clienteSeleccionado)
      );
    }
    setVales(valesFiltrados);
    setPaginaActual(1);
  }, [clienteSeleccionado, valesOriginales]);

  useEffect(() => {
    manejadorFiltroClienteSeleccionado();
  }, [manejadorFiltroClienteSeleccionado]);

  const manejarFiltro = async () => {
    try {
      if (!esFechaValida(fechaDesde) || !esFechaValida(fechaHasta)) {
        alert("Ingrese una fecha válida.");
        return;
      }

      const respuesta = await fetch(
        `${apiUrl}/caja/vales_filtrados`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fechaDesde,
            fechaHasta,
            sucursalId: buscarSucursal,
          }),
          credentials: "include",
        }
      );
      if (respuesta.ok) {
        const datos = await respuesta.json();
        if (datos.length === 0) {
          alert("No existe información para la fecha indicada.");
          return;
        }
        setVales(datos);
        setValesOriginales(datos);
        setPaginaActual(1);
        setClienteSeleccionado("");

        const clientes = datos.map((vale) => parseInt(vale.cliente_id));
        const clientesUnicos = [...new Set(clientes)];

        const clientesFiltrados =
          contexto.clientesTabla
            .filter(
              (cliente) =>
                clientesUnicos.includes(
                  parseInt(cliente.id)
                )
            )
            .sort(
              (a, b) => {

                const nombreA =
                  `${a.apellido || ""} ${a.nombre || ""}`;

                const nombreB =
                  `${b.apellido || ""} ${b.nombre || ""}`;

                return nombreA.localeCompare(
                  nombreB,
                  "es",
                  {
                    sensitivity: "base",
                  }
                );
              }
            );

        setClientesFiltrados(
          clientesFiltrados
        );
      } else {
        throw new Error("Error al obtener los vales");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const manejarOrden = (nombreColumna) => {
    setDireccionOrden(
      nombreColumna === columnaOrden && direccionOrden === "asc"
        ? "desc"
        : "asc"
    );
    setColumnaOrden(nombreColumna);

    const valesOrdenados = [...vales].sort((a, b) => {
      let valorA = a[nombreColumna];
      let valorB = b[nombreColumna];

      if (nombreColumna === "importe") {
        valorA = parseFloat(valorA);
        valorB = parseFloat(valorB);
      }

      if (valorA < valorB) {
        return direccionOrden === "asc" ? -1 : 1;
      } else if (valorA > valorB) {
        return direccionOrden === "asc" ? 1 : -1;
      } else {
        return 0;
      }
    });

    setVales(valesOrdenados);
  };

  const esFechaValida = (cadenaFecha) => {
    const regEx = /^\d{4}-\d{2}-\d{2}$/;
    if (!cadenaFecha.match(regEx)) return false;
    const fecha = new Date(cadenaFecha);
    if (!fecha.getTime()) return false;
    return fecha.toISOString().slice(0, 10) === cadenaFecha;
  };

  const manejarBusqueda = () => {
    manejarFiltro();
  };

  const indiceUltimoVale = paginaActual * valesPorPagina;
  const indicePrimerVale = indiceUltimoVale - valesPorPagina;
  const valesActuales = vales.slice(indicePrimerVale, indiceUltimoVale);
  const subtotalVales =
    vales.reduce(
      (total, vale) =>
        total +
        (Number(vale.importecupon) || 0),
      0
    );

  const paginaSiguiente = () => {
    if (paginaActual < Math.ceil(vales.length / valesPorPagina)) {
      setPaginaActual(paginaActual + 1);
    }
  };

  const paginaAnterior = () => {
    if (paginaActual > 1) {
      setPaginaActual(paginaActual - 1);
    }
  };

  const exportarExcel = () => {
    const valesParaExportar = vales.map((vale) => {
      const sucursalNombre = contexto.sucursalesTabla.find(
        (sucursal) => sucursal.id === parseInt(vale.sucursal_id)
      )?.nombre || "Desconocido";

      const clienteNombre = contexto.clientesTabla.find(
        (cliente) => cliente.id === parseInt(vale.cliente_id)
      );
      const clienteCompleto = clienteNombre
        ? `${clienteNombre.nombre} ${clienteNombre.apellido}`
        : "Desconocido";

      return {
        Fecha: formatearFecha(vale.fecha),
        Importe: vale.importecupon,
        Sucursal: sucursalNombre,
        Cliente: clienteCompleto,
      };
    });

    const ws = XLSX.utils.json_to_sheet(valesParaExportar);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vales");
    XLSX.writeFile(wb, "vales.xlsx");
  };

  return (
    <Container className="vt-page">
      <h1 className="my-list-title dark-text vt-title">Vales</h1>

      {/* Filtros */}
      <div className="vt-toolbar mb-3 d-flex flex-wrap align-items-end gap-3">
        <div className="d-inline-block w-auto mx-2">
          <label className="mr-2">DESDE:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="form-control rounded-0 text-center vt-input"
          />
        </div>

        <div className="d-inline-block w-auto mx-2">
          <label className="ml-2 mr-2">HASTA:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="form-control rounded-0 text-center vt-input"
          />
        </div>

        <div className="d-inline-block">
          <label className="d-block">Sucursal</label>
          <FormControl
            as="select"
            value={buscarSucursal}
            onChange={(e) => setBuscarSucursal(e.target.value)}
            className="vt-input"
            style={{ minWidth: 240 }}
          >
            <option value="">Seleccione una sucursal</option>
            {contexto.sucursalesTabla.map((sucursal) => (
              <option key={sucursal.id} value={sucursal.id}>
                {sucursal.nombre}
              </option>
            ))}
          </FormControl>
        </div>

        <div className="d-inline-block">
          <label className="d-block">Empleado</label>
          <FormControl
            as="select"
            value={clienteSeleccionado}
            onChange={(e) => setClienteSeleccionado(e.target.value)}
            className="vt-input"
            style={{ minWidth: 280 }}
          >
            <option value="">Seleccione un cliente</option>
            {clientesFiltrados.map((cliente) => (
              <option
                key={cliente.id}
                value={cliente.id}
              >
                {cliente.apellido}{" "}
                {cliente.nombre}
              </option>
            ))}
          </FormControl>
        </div>

        <div className="d-inline-block mx-2">
          <Button onClick={manejarBusqueda} className="vt-btn">Filtrar</Button>
        </div>

        <div className="d-inline-block">
          <Button onClick={exportarExcel} disabled={vales.length === 0} className="vt-btn-out">
            Exportar a Excel
          </Button>
        </div>
      </div>

      {/* Selector de tamaño de página */}
      <div className="mb-2">
        <label className="me-2">Por página</label>
        <FormControl
          as="select"
          value={valesPorPagina}
          onChange={(e) => setValesPorPagina(parseInt(e.target.value))}
          className="vt-input d-inline-block"
          style={{ width: 120 }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </FormControl>
      </div>

      {/* Tabla */}
      <div className="vt-tablewrap table-responsive">
        <Table striped bordered hover className="mb-2">
          <thead>
            <tr>
              <th onClick={() => manejarOrden("fecha")} className="vt-th-sort">
                Fecha
              </th>
              <th onClick={() => manejarOrden("importecupon")} className="vt-th-sort text-end">
                Importe
              </th>
              <th onClick={() => manejarOrden("sucursal_id")} className="vt-th-sort">
                Sucursal
              </th>
              <th onClick={() => manejarOrden("cliente_id")} className="vt-th-sort">
                Cliente
              </th>
            </tr>
          </thead>
          <tbody>
            {valesActuales.map((vale) => {
              const sucursalNombre =
                contexto.sucursalesTabla.find((s) => s.id === parseInt(vale.sucursal_id))?.nombre ||
                "Desconocido";

              const clienteNombre = contexto.clientesTabla.find(
                (c) => c.id === parseInt(vale.cliente_id)
              );
              const clienteCompleto = clienteNombre
                ? `${clienteNombre.nombre} ${clienteNombre.apellido}`
                : "Desconocido";

              return (
                <tr key={vale.id}>
                  <td>{formatearFecha(vale.fecha)}</td>
                  <td className="text-end">{vale.importecupon}</td>
                  <td>{sucursalNombre}</td>
                  <td>{clienteCompleto}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div
        className="d-flex justify-content-end align-items-center mb-3"
      >
        <div
          className="fw-bold fs-5"
        >
          Subtotal:{" "}
          {subtotalVales.toLocaleString(
            "es-AR",
            {
              style: "currency",
              currency: "ARS",
            }
          )}
        </div>
      </div>

      {/* Paginación */}

      {/* Paginación */}
      <div className="d-flex justify-content-center align-items-center vt-pager">
        <Button onClick={paginaAnterior} disabled={paginaActual === 1} variant="light">
          <BsChevronLeft />
        </Button>
        <span className="mx-2">
          Página {paginaActual} de {Math.ceil(vales.length / valesPorPagina)}
        </span>
        <Button
          onClick={paginaSiguiente}
          disabled={paginaActual === Math.ceil(vales.length / valesPorPagina)}
          variant="light"
        >
          <BsChevronRight />
        </Button>
      </div>
    </Container>
  );

}

